import React, { useState } from 'react'
import { useAuth } from '../lib/hooks'
import { LoginResult } from '../types'

interface Props {
  challengeToken: string
  onSession: () => void
  onPasswordChangeRequired: (changeToken: string) => void
  onCancel: () => void
}

/**
 * Schritt 2 vom Login-Flow: User hat Passwort eingegeben, Backend hat MFA
 * verlangt. Hier verifiziert er TOTP-Code (6 Ziffern) ODER Backup-Code (8 Zeichen).
 * Backend antwortet entweder mit fertiger Session (→ onSession), oder mit
 * password-change-required (→ onPasswordChangeRequired).
 */
export function MfaChallenge({
  challengeToken,
  onSession,
  onPasswordChangeRequired,
  onCancel,
}: Props) {
  const { verifyMfa } = useAuth()
  const [code, setCode] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting || !code.trim()) return
    setSubmitting(true)
    setError('')
    try {
      const result: LoginResult = await verifyMfa(challengeToken, code.trim())
      if (result.kind === 'session') {
        onSession()
      } else if (result.kind === 'password_change_required') {
        onPasswordChangeRequired(result.changeToken)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{ width: '100%', maxWidth: 380 }}>
      <h1
        style={{
          fontSize: 24,
          fontWeight: 600,
          letterSpacing: '-0.02em',
          color: 'var(--ink)',
          margin: '0 0 6px',
        }}
      >
        Zwei-Faktor-Authentifizierung
      </h1>
      <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: '0 0 28px' }}>
        Code aus deiner Authenticator-App eingeben (oder einen Backup-Code).
      </p>

      {error && (
        <div
          role="alert"
          style={{
            marginBottom: 18,
            padding: '10px 12px',
            background: 'var(--bad-bg)',
            color: 'var(--bad)',
            border: '1px solid color-mix(in srgb, var(--bad) 30%, transparent)',
            borderRadius: 8,
            fontSize: 13,
            lineHeight: 1.4,
          }}
        >
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div style={{ marginBottom: 14 }}>
          <label
            htmlFor="mfa-code"
            style={{
              display: 'block',
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--ink-2)',
              marginBottom: 6,
              letterSpacing: '0.01em',
            }}
          >
            Code
          </label>
          <input
            id="mfa-code"
            name="mfa-code"
            type="text"
            inputMode="text"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={submitting}
            autoFocus
            required
            placeholder="123456 oder Backup-Code"
            className="sk-input"
            style={{
              width: '100%',
              fontSize: 14,
              padding: '9px 12px',
              fontFamily: 'var(--mono)',
              letterSpacing: '0.08em',
              cursor: submitting ? 'not-allowed' : 'text',
            }}
          />
        </div>

        <button
          type="submit"
          disabled={submitting || !code.trim()}
          className="sk-btn primary"
          style={{
            width: '100%',
            justifyContent: 'center',
            padding: '11px 16px',
            fontSize: 14,
            marginTop: 8,
          }}
        >
          {submitting ? 'Prüfen…' : 'Bestätigen'}
        </button>
      </form>

      <button
        type="button"
        onClick={onCancel}
        disabled={submitting}
        style={{
          marginTop: 18,
          background: 'transparent',
          border: 'none',
          color: 'var(--ink-3)',
          fontSize: 12,
          cursor: submitting ? 'not-allowed' : 'pointer',
          textDecoration: 'underline',
        }}
      >
        Zurück zum Login
      </button>
    </div>
  )
}
